import React, { useMemo } from 'react'
import './order-detail.scss'
import { BaseDrawer } from '@/components/drawer/base-drawer'
import { layerManage, showLayer } from '@/utils/layer'
import { useRequest } from 'ahooks'
import { $post } from '@/api'
import { ICashierOrder } from '../order-table/type'
import { Column, Row, Expaned } from '@/components/layout/layout'
import useBemClass from '@/utils/hooks/useBEMClass'
import FormTitle from '@/views/main/modules/goods-manage/components/form/form-title/form-title'
import { Spin, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import CashierOrderRefund from '../../order-refund/order-refund'
import { Modal } from '@/components/dialog/modal'
import { cashierOrderStore } from '../../store'
import Tip from '@/components/tip/tip'

interface CashierOrderDetailProps {
    orderNo: string
}

const CashierOrderDetail: React.FC<CashierOrderDetailProps> = props => {
    const cls = useBemClass('cashier-order-detail')

    const orderDetailReq = useRequest<{
        orders: ICashierOrder.Order[]
    }>(() =>
        $post({
            url: '/order/orderdatalist',
            params: {
                order_no: props.orderNo,
            },
        }),
    )

    const orderDetail = useMemo(() => {
        if (orderDetailReq.data) {
            return orderDetailReq.data.orders[0] as ICashierOrder.Order
        } else {
            return null
        }
    }, [orderDetailReq.data])

    const paymentText = useMemo(() => {
        if (orderDetail) {
            return orderDetail.payments.map(p => `${p.payment_type_text}：¥${p.payment_amount}`).join(' | ')
        } else {
            return ''
        }
    }, [orderDetail])

    const columns: ColumnsType<ICashierOrder.GoodsList> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品名',
            key: 'goods',
            render: (val, r) => r.goods.name,
        },
        {
            title: '数/重量',
            key: 'amount',
            render: (val, r) => r.amount + r.settle_unit.name,
        },
        {
            title: '小计',
            key: 'subtotal',
            dataIndex: 'subtotal',
            align: 'right',
            render: v => `¥${v}`,
        },
    ]

    const printOrderReq = useRequest(
        () =>
            $post({
                url: '/mall/apporderprint',
                params: {
                    order_no: orderDetail?.order_no,
                    order_type: 1,
                },
            }),
        {
            manual: true,
            onError: e => $msg('error', e.message),
            onSuccess: () => {
                $msg('success', '小票打印中')
            },
        },
    )

    const refundReq = useRequest(
        () =>
            $post({
                url: '/order/refundorder',
                params: {
                    order_id: orderDetail?.id,
                    order_type: 2,
                    refund_goods: null,
                    refund_subtotal: orderDetail?.actual_payment,
                    refund_type: null,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '退款成功')
                layerManage.pop()
                cashierOrderStore.setRefresh()
            },
            onError: e => $msg('error', e.message),
        },
    )

    return (
        <BaseDrawer
            isShow
            title="门店订单详情"
            width={580}
            onClose={() => {
                layerManage.pop()
            }}
        >
            <Column data-height-100p>
                <Expaned data-overflow-auto>
                    <Spin spinning={orderDetailReq.loading}>
                        <div className={cls('member-container')}>
                            <Column data-justify-content-center data-expaned>
                                <div className={cls('list-item')}>
                                    <div className="title coll">会员名称：</div>
                                    <div className="txt">{orderDetail?.member.name || '-'}</div>
                                </div>
                            </Column>
                            <Column data-justify-content-center data-expaned>
                                <div className={cls('list-item')}>
                                    <div className="title coll">手机号：</div>
                                    <div className="txt">{orderDetail?.member.phone || '-'}</div>
                                </div>
                            </Column>
                        </div>
                        <div className={cls('container')}>
                            <Column data-justify-content-center data-expaned>
                                <div className={cls('list-item')}>
                                    <div className="title coll">订单编号：</div>
                                    <div className="txt">{orderDetail?.order_no}</div>
                                </div>

                                <div data-margin-top-12 className={cls('list-item')}>
                                    <div className="title coll">收银员：</div>
                                    <Row className="txt" data-align-items-center>
                                        <img
                                            data-margin-right-6
                                            src={orderDetail?.creator?.user.avatar}
                                            alt=""
                                            className="avatar"
                                        />
                                        <div data-width-126 className="text-popper">
                                            {orderDetail?.creator?.user.realname || orderDetail?.creator?.user.nickname}
                                        </div>
                                    </Row>
                                </div>
                            </Column>
                            <Column data-justify-content-center data-expaned>
                                <div className={cls('list-item')}>
                                    <div className="title coll">结算时间：</div>
                                    <div className="txt">{orderDetail?.create_time}</div>
                                </div>
                            </Column>
                        </div>
                        <FormTitle title={`商品信息（${orderDetail?.goods_list.length || 0}）`} hasLine={false} />
                        <div className={cls('table-container')}>
                            <Table
                                columns={columns}
                                rowKey={r => r.id}
                                dataSource={orderDetail?.goods_list}
                                pagination={false}
                                bordered
                                scroll={{ x: 'max-content', y: 344 }}
                            ></Table>
                            <div className={cls('table-bottom')}>
                                <div className={cls('table-bottom', 'pay')}>{paymentText}</div>
                                <div className={cls('table-bottom', 'amount')}>
                                    实付：¥{orderDetail?.actual_payment}
                                </div>
                            </div>
                        </div>
                    </Spin>
                </Expaned>
                <div className={cls('footer')}>
                    <Row data-align-items-center className={cls('footer', 'left')} onClick={printOrderReq.run}>
                        <i className="iconfont icondayin2" data-font-size-20 />
                        <span data-font-size-16 data-margin-left-4>
                            打印小票
                        </span>
                        <div datamargin-left-4>
                            <Tip text="通过收银台打印" />
                        </div>
                    </Row>
                    <Button
                        hidden={!!orderDetail?.refund_orders.length}
                        types="error"
                        disabled={!orderDetail}
                        onClick={() => {
                            layerManage.pop()
                            showLayer(
                                orderDetail?.goods_list.length ? (
                                    <CashierOrderRefund
                                        orderId={orderDetail?.id || 0}
                                        orderType={orderDetail!.id}
                                        payments={orderDetail?.payments || []}
                                        goodList={orderDetail?.goods_list || []}
                                    />
                                ) : (
                                    <Modal
                                        headerStyles={{ width: '440px' }}
                                        isShow
                                        type="danger"
                                        style={{ height: '156px' }}
                                        onClose={() => layerManage.pop()}
                                        onCancel={() => layerManage.pop()}
                                        onConfirm={() => {
                                            layerManage.pop()
                                            refundReq.run()
                                        }}
                                    >
                                        <div>
                                            <div data-font-size-20 data-font-weight-600 data-color-000>
                                                确认退款吗？
                                            </div>
                                            <div data-font-size-14 data-color-ff4343 data-margin-top-8>
                                                ¥{orderDetail?.actual_payment}将以原支付方式退回？
                                            </div>
                                        </div>
                                    </Modal>
                                ),
                            )
                        }}
                    >
                        退款
                    </Button>
                </div>
            </Column>
        </BaseDrawer>
    )
}

export default CashierOrderDetail
